<template>
  <div>
    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0"
    >
      <div class="m-2">
        <!-- Table Top Controls -->
        <b-row class="align-items-center">
          <!-- Per Page Selector -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mr-1 mb-0">{{ $t('Show') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
            />
            <label class="ml-1 mb-0">{{ $t('entries') }}</label>
          </b-col>

          <!-- Search & Add Plan Button -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-md-end">
              <b-input-group class="input-group-merge mr-1">
                <b-input-group-prepend is-text>
                  <feather-icon
                    icon="SearchIcon"
                    size="14"
                    class="text-muted"
                  />
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchQuery"
                  :placeholder="$t('Search')"
                  class="search-input"
                />
              </b-input-group>

              <b-button
                variant="primary"
                :to="{ name: 'register-maintenance-plan' }"
                class="text-nowrap d-flex align-items-center"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="16"
                  class="mr-50"
                />
                <span>{{ $t('Add Maintenance Plan') }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Main Table -->
      <b-table
        ref="refMaintenancePlanListTable"
        class="position-relative"
        :items="fetchAllMaintenancePlan"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="emptyText"
        :sort-desc.sync="isSortDirDesc"
      >
        <!-- Column: Maintenance Plan Name -->
        <template #head(name)>
          {{ $t('Maintenance Plan') }}
        </template>
        <template #cell(name)="data">
          <div class="d-flex align-items-center py-50">
            <b-avatar
              variant="light-primary"
              rounded
              size="38"
              class="mr-1 flex-shrink-0 cursor-pointer"
              :to="{ name: 'register-maintenance-plan', params: { id: data.item.id } }"
            >
              <feather-icon
                icon="ClipboardIcon"
                size="18"
              />
            </b-avatar>
            <div class="d-flex flex-column text-truncate">
              <b-link
                :to="{ name: 'register-maintenance-plan', params: { id: data.item.id } }"
                class="font-weight-bolder text-body plan-title-link text-truncate"
                :title="data.item.name"
              >
                {{ data.item.name }}
              </b-link>
              <small
                v-if="data.item.customerName"
                class="text-muted text-truncate d-md-none"
              >
                <feather-icon
                  icon="BriefcaseIcon"
                  size="11"
                  class="mr-25 align-middle"
                />
                {{ data.item.customerName }}
              </small>
            </div>
          </div>
        </template>

        <!-- Column: Customer / Owner -->
        <template #head(customerName)>
          {{ $t('Customer') }}
        </template>
        <template #cell(customerName)="data">
          <div class="d-flex align-items-center text-nowrap py-50">
            <feather-icon
              icon="BriefcaseIcon"
              size="14"
              class="mr-50 text-muted"
            />
            <span class="font-weight-medium text-body">
              {{ data.item.customerName || '-' }}
            </span>
          </div>
        </template>

        <!-- Column: Steps Count -->
        <template #head(stepsCount)>
          {{ $t('Steps') }}
        </template>
        <template #cell(stepsCount)="data">
          <div class="text-center py-50">
            <b-link
              v-b-tooltip.hover
              :to="{ name: 'register-maintenance-plan', params: { id: data.item.id }, query: { tab: 'steps' } }"
              class="text-decoration-none"
              :title="$t('Plan Steps')"
            >
              <b-badge
                pill
                :variant="Number(data.item.stepsCount) > 0 ? 'light-info' : 'light-secondary'"
                class="font-weight-bolder px-1 py-50 d-inline-flex align-items-center cursor-pointer badge-metric"
              >
                <feather-icon
                  icon="LayersIcon"
                  size="13"
                  class="mr-50"
                />
                <span>
                  {{ data.item.stepsCount || 0 }} {{ Number(data.item.stepsCount) === 1 ? $t('Step') : $t('Steps') }}
                </span>
              </b-badge>
            </b-link>
          </div>
        </template>

        <!-- Column: Linked Assets Count -->
        <template #head(assetsCount)>
          {{ $t('Linked Assets') }}
        </template>
        <template #cell(assetsCount)="data">
          <div class="text-center py-50">
            <b-link
              v-b-tooltip.hover
              :to="{ name: 'register-maintenance-plan', params: { id: data.item.id }, query: { tab: 'assets' } }"
              class="text-decoration-none"
              :title="$t('Linked Assets')"
            >
              <b-badge
                pill
                :variant="Number(data.item.assetsCount) > 0 ? 'light-success' : 'light-secondary'"
                class="font-weight-bolder px-1 py-50 d-inline-flex align-items-center cursor-pointer badge-metric"
              >
                <feather-icon
                  icon="TruckIcon"
                  size="13"
                  class="mr-50"
                />
                <span>
                  {{ data.item.assetsCount || 0 }} {{ Number(data.item.assetsCount) === 1 ? $t('Asset') : $t('Assets') }}
                </span>
              </b-badge>
            </b-link>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #head(actions)>
          {{ $t('Actions') }}
        </template>
        <template #cell(actions)="data">
          <div class="actions-cell d-flex align-items-center justify-content-end">
            <!-- Dropdown Options no mesmo padrão de AssetList.vue -->
            <b-dropdown
              variant="link"
              toggle-class="action-menu-btn"
              no-caret
              :right="$store.state.appConfig.isRTL"
              boundary="viewport"
              :popper-opts="{
                strategy: 'fixed',
                placement: $store.state.appConfig.isRTL ? 'bottom-start' : 'bottom-end',
                modifiers: [{ name: 'flip', enabled: false }],
              }"
              @show="onDropdownShow"
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="15"
                  class="text-body"
                />
              </template>

              <b-dropdown-item :to="{ name: 'register-maintenance-plan', params: { id: data.item.id } }">
                <feather-icon
                  icon="EditIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Edit') }}</span>
              </b-dropdown-item>

              <b-dropdown-item :to="{ name: 'register-maintenance-plan', params: { id: data.item.id }, query: { tab: 'steps' } }">
                <feather-icon
                  icon="LayersIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Steps') }}</span>
              </b-dropdown-item>

              <b-dropdown-item :to="{ name: 'register-maintenance-plan', params: { id: data.item.id }, query: { tab: 'assets' } }">
                <feather-icon
                  icon="TruckIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Linked Assets') }}</span>
              </b-dropdown-item>

              <b-dropdown-item :to="{ name: 'maintenance-assets', params: { id: data.item.id } }">
                <feather-icon
                  icon="ArrowRightCircleIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Open') }}</span>
              </b-dropdown-item>

              <b-dropdown-divider />

              <b-dropdown-item
                variant="danger"
                @click="modalDelete(data.item.id)"
              >
                <feather-icon
                  icon="Trash2Icon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('Delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>

        <!-- Empty State Slot -->
        <template #empty>
          <div class="text-center py-3">
            <feather-icon
              icon="InboxIcon"
              size="36"
              class="text-muted mb-1"
            />
            <p class="text-muted mb-0 font-weight-bold">
              {{ emptyText }}
            </p>
          </div>
        </template>
      </b-table>

      <!-- Pagination & Metadata Bottom Bar -->
      <div class="mx-2 mb-2">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start mb-1 mb-sm-0"
          >
            <span class="text-muted">
              {{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }} {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}
            </span>
          </b-col>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalMaintenancePlan"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
/* eslint-disable import/no-cycle */
import {
  BCard,
  BRow,
  BCol,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BButton,
  BTable,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BPagination,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import {
  computed,
  onUnmounted,
  nextTick,
} from '@vue/composition-api'
import useMaintenancePlanList from './useMaintenancePlanList'
import managementStoreModule from '../managementStoreModule'

export default {
  name: 'MaintenancePlanList',
  components: {
    BCard,
    BRow,
    BCol,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    BButton,
    BTable,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BPagination,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup(props, { root }) {
    const MANAGEMENT_APP_STORE_MODULE_NAME = 'app-management'

    // Register store module if needed
    if (!store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANAGEMENT_APP_STORE_MODULE_NAME, managementStoreModule)
    }

    // Unregister module on leave
    onUnmounted(() => {
      if (store.hasModule(MANAGEMENT_APP_STORE_MODULE_NAME)) {
        store.unregisterModule(MANAGEMENT_APP_STORE_MODULE_NAME)
      }
    })

    const {
      fetchAllMaintenancePlan,
      tableColumns,
      perPage,
      currentPage,
      totalMaintenancePlan,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMaintenancePlanListTable,
      refetchData,
    } = useMaintenancePlanList()

    const emptyText = computed(() => root.$t('No matching records found'))

    const deletePlan = planId => {
      store
        .dispatch('app-management/deleteMaintenancePlan', {
          id: planId,
        })
        .then(() => {
          root.$swal(root.$t('Deleted'), `${root.$t('You successfully deleted')}`, 'success')
          refetchData()
        })
        .catch(() => {
          root.$swal(root.$t('Error'), root.$t('An error occurred while trying to delete'), 'error')
        })
    }

    const modalDelete = planId => {
      root.$swal({
        title: root.$t('Are you sure?'),
        text: root.$t("You can't revert your action"),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: root.$t('Yes'),
        cancelButtonText: root.$t('No'),
        showCloseButton: true,
        showLoaderOnConfirm: true,
        customClass: {
          cancelButton: 'cancelBtn',
          confirmButton: 'mr-1',
        },
      }).then(result => {
        if (result.value) {
          deletePlan(planId)
        }
      })
    }

    const onDropdownShow = bvEvent => {
      nextTick(() => {
        const toggle = bvEvent.target
        if (!toggle) return
        const rect = toggle.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom - 8
        const btnGroup = toggle.closest('.btn-group')
        const menu = btnGroup ? btnGroup.querySelector('.dropdown-menu') : null

        if (menu) {
          menu.style.maxHeight = `${Math.max(spaceBelow, 120)}px`
          menu.style.overflowY = 'auto'
        }
      })
    }

    return {
      fetchAllMaintenancePlan,
      tableColumns,
      perPage,
      currentPage,
      totalMaintenancePlan,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refMaintenancePlanListTable,
      refetchData,
      emptyText,
      modalDelete,
      deletePlan,
      onDropdownShow,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.per-page-selector {
  width: 90px;
}

.plan-title-link {
  font-weight: 600;
  color: #5e5873;
  transition: color 0.15s ease-in-out;

  &:hover {
    color: #7367f0 !important;
  }
}

.badge-metric {
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }
}

// Actions cell & menu button (mesmo padrão de AssetList.vue)
.actions-cell {
  white-space: nowrap;
}

.action-menu-btn {
  padding: 4px 6px !important;
  border-radius: 6px !important;
  color: #b9b9c3;
  transition: background 0.12s ease, color 0.12s ease !important;

  &:hover {
    background: rgba(115, 103, 240, 0.08) !important;
    color: #7367f0 !important;
  }
}

::v-deep .table-responsive {
  margin-bottom: 0;

  .table {
    margin-bottom: 0;
  }
}
</style>
